import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { DatePickerWithPresets } from "@/components/ui/date-picker-with-presets";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/use-toast";
import {
  CreateMatchingOverrideData,
  MentorSlot,
  useCreateMatchingOverride,
} from "@/lib/api/user";
import { useSignedMedia } from "@/lib/hooks/use-signed-media";
import { countWords, getInitials } from "@/lib/utils";
import { format } from "date-fns";
import { CheckCircle2, Loader2, X } from "lucide-react";
import { useState } from "react";

const REASON_WORD_LIMIT = 50;

interface MatchingOverrideDialogProps {
  mentorSlot: MentorSlot;
  isOpen: boolean;
  onClose: () => void;
  forceOpenAllocation?: number | null;
}

export function MatchingOverrideDialog({
  mentorSlot,
  isOpen,
  onClose,
  forceOpenAllocation,
}: MatchingOverrideDialogProps) {
  const isCurrentlyOpen = mentorSlot.slot_config.matching_status === "open";
  // open mentors can only be force-paused; paused mentors can go either way
  const [direction, setDirection] = useState<"open" | "pause">(
    isCurrentlyOpen ? "pause" : "open",
  );
  const isPause = direction === "pause";
  const createOverride = useCreateMatchingOverride();
  const { toast } = useToast();
  const [endDate, setEndDate] = useState<Date | undefined>();
  const [reason, setReason] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
  const avatarUrl = useSignedMedia(mentorSlot.mentor.profile_picture_url);

  const handleSubmit = async () => {
    const data: CreateMatchingOverrideData = {
      direction,
    };
    if (isPause && endDate) {
      data.end_date = format(endDate, "yyyy-MM-dd");
    }
    const trimmedReason = reason.trim();
    if (trimmedReason) {
      data.reason = trimmedReason;
    }

    try {
      await createOverride.mutateAsync({
        userId: mentorSlot.mentor.id,
        data,
      });
      setShowSuccess(true);
    } catch (_error) {
      toast({
        title: "Error",
        description: "Failed to create override",
        variant: "destructive",
      });
    }
  };

  const successDescription =
    isPause && endDate
      ? `Daily slots for ${mentorSlot.mentor.name} have been turned off until ${format(
          endDate,
          "MMM d, yyyy",
        )}.`
      : `Daily slots for ${mentorSlot.mentor.name} have been turned ${
          isPause ? "off" : "on"
        }.`;

  const footerText = isPause
    ? "Daily slots will be disabled until the selected date"
    : `This mentor will be able to receive ${
        forceOpenAllocation ?? 10
      } more teens`;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="gap-0 p-0 sm:max-w-md sm:rounded-xl"
        hideCloseButton
      >
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-5">
          <DialogTitle className="text-xl font-bold tracking-tight">
            {showSuccess
              ? "Override Applied"
              : isPause
                ? "Pause daily slots"
                : "Open daily slots"}
          </DialogTitle>
          <DialogClose className="opacity-70 transition-opacity hover:opacity-100 focus:outline-none">
            <X className="h-5 w-5" />
            <span className="sr-only">Close</span>
          </DialogClose>
        </div>

        {showSuccess ? (
          <div className="p-6">
            <div className="flex flex-col items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-50">
                <CheckCircle2 className="h-6 w-6 text-green-600" />
              </div>
              <div className="flex flex-col items-center gap-1 text-center">
                <p className="text-base font-bold tracking-tight">
                  Override Applied
                </p>
                <p className="text-sm text-gray-600">{successDescription}</p>
              </div>
              <Button className="h-10 w-full rounded-lg" onClick={onClose}>
                Done
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-5 p-6">
            <div className="flex items-center gap-3">
              <Avatar className="h-8 w-8">
                <AvatarImage src={avatarUrl?.url ?? ""} />
                <AvatarFallback className="text-xs">
                  {getInitials(mentorSlot.mentor.name)}
                </AvatarFallback>
              </Avatar>
              <span className="text-sm">{mentorSlot.mentor.name}</span>
            </div>

            {!isCurrentlyOpen && (
              <RadioGroup
                value={direction}
                onValueChange={(value) =>
                  setDirection(value as "open" | "pause")
                }
                className="flex flex-col gap-2"
              >
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="open" id="override-direction-open" />
                  <Label
                    htmlFor="override-direction-open"
                    className="text-sm font-normal"
                  >
                    Open daily slots
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="pause" id="override-direction-pause" />
                  <Label
                    htmlFor="override-direction-pause"
                    className="text-sm font-normal"
                  >
                    Pause daily slots
                  </Label>
                </div>
              </RadioGroup>
            )}

            {isPause && (
              <div className="flex flex-col gap-2">
                <Label className="text-sm font-normal">End date</Label>
                <DatePickerWithPresets
                  date={endDate}
                  onDateChange={setEndDate}
                  className="h-11 w-full rounded-lg px-3"
                  showCalendarIcon
                  modal
                />
              </div>
            )}

            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-normal">
                  Add a note (optional)
                </Label>
                <span
                  className={
                    countWords(reason) >= REASON_WORD_LIMIT
                      ? "text-xs text-red-500"
                      : "text-xs text-gray-400"
                  }
                >
                  {countWords(reason)}/{REASON_WORD_LIMIT} words
                </span>
              </div>
              <Textarea
                value={reason}
                onChange={(e) => {
                  const value = e.target.value;
                  if (countWords(value) <= REASON_WORD_LIMIT) {
                    setReason(value);
                    return;
                  }
                  setReason(
                    value
                      .trim()
                      .split(/\s+/)
                      .slice(0, REASON_WORD_LIMIT)
                      .join(" "),
                  );
                }}
                placeholder={
                  isPause
                    ? "eg. On PTO until end of month"
                    : "eg. mentor requested more teens"
                }
                className="h-[120px] resize-none rounded-xl p-4"
              />
            </div>

            <div className="flex flex-col gap-2">
              <p className="text-xs text-gray-400">{footerText}</p>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="h-10 rounded-lg"
                  onClick={onClose}
                >
                  Cancel
                </Button>
                <Button
                  className="h-10 rounded-lg"
                  onClick={handleSubmit}
                  disabled={createOverride.isPending || (isPause && !endDate)}
                >
                  {createOverride.isPending && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  Confirm Override
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
